// src/pages/NotificationBell.js (수정된 버전)
import React, { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  addDoc,
  getDoc,
  serverTimestamp,
  orderBy,
  limit,
  deleteDoc
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

function NotificationBell() {
  const [user, setUser] = useState(null);

  // 사용자 정보 로드
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    setUser(storedUser);
  }, []);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [rejectReason, setRejectReason] = useState(""); 
  const [activeRejectId, setActiveRejectId] = useState(""); 
  const [loading, setLoading] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);

  const bellRef = useRef();
  const navigate = useNavigate();

  // 스타일 정의 (함수 내부로 이동)
  const acceptButtonStyle = {
    background: "linear-gradient(135deg, #4caf50, #45a049)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 2px 4px rgba(76,175,80,0.2)"
  };

  const rejectButtonStyle = {
    background: "linear-gradient(135deg, #f44336, #e53935)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 2px 4px rgba(244,67,54,0.2)"
  };

  const inputStyle = {
    width: "100%",
    borderRadius: 6,
    border: "1px solid #ddd",
    padding: "6px 8px",
    fontSize: 12,
    marginTop: 4,
    boxSizing: "border-box"
  };

  // 알림 데이터 불러오기 (모든 타입 포함)
  useEffect(() => {
    if (!user?.id) return;
    
    const fetchNotifications = async () => {
      try {
        const q = query(
          collection(db, "notifications"),
          where("to", "==", user.id),
          where("status", "in", ["pending", "unread"]), // pending과 unread 모두 포함
          orderBy("createdAt", "desc"),
          limit(50) // 더 많은 알림 로드
        );
        const snap = await getDocs(q);
        const notifs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
        setNotifications(notifs);
      } catch (error) {
        console.error("알림 로딩 실패:", error);
        setNotifications([]);
      }
    };

    setLoading(true);
    fetchNotifications().finally(() => setLoading(false));
    
    if (showDropdown) {
      fetchNotifications();
    }
  }, [user?.id, showDropdown]);

  // 알림 클릭시 읽음 처리
  const markAsRead = async (notifId) => {
    try {
      await updateDoc(doc(db, "notifications", notifId), { 
        status: "read",
        readAt: serverTimestamp()
      });
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (error) {
      console.error("알림 읽음 처리 실패:", error);
    }
  };

  // 알림 드롭다운 외부 클릭시 닫힘
  useEffect(() => {
    function handleClick(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setShowDropdown(false);
        setActiveRejectId("");
        setRejectReason("");
      }
    }
    
    if (showDropdown) {
      document.addEventListener("mousedown", handleClick);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [showDropdown]);

  // 주기적 알림 체크 (백그라운드에서)
  useEffect(() => {
    if (!user?.id) return;

    const checkNotifications = async () => {
      try {
        const q = query(
          collection(db, "notifications"),
          where("to", "==", user.id),
          where("status", "in", ["pending", "unread"]),
          limit(1)
        );
        const snap = await getDocs(q);
        if (snap.size !== notifications.length && !showDropdown) {
          const fullQ = query(
            collection(db, "notifications"),
            where("to", "==", user.id),
            where("status", "in", ["pending", "unread"]),
            orderBy("createdAt", "desc"),
            limit(50)
          );
          const fullSnap = await getDocs(fullQ);
          const notifs = fullSnap.docs.map((d) => ({ ...d.data(), id: d.id }));
          setNotifications(notifs);
        }
      } catch (error) {
        console.error("백그라운드 알림 체크 실패:", error);
      }
    };

    const interval = setInterval(() => {
      if (!showDropdown) {
        checkNotifications();
      }
    }, 30000); // 30초마다 체크

    return () => clearInterval(interval);
  }, [user?.id, notifications.length, showDropdown]);

  const handleBellClick = () => {
    setShowDropdown((prev) => !prev);
    if (!showDropdown) {
      setLoading(true);
    }
  };

  // === 기존 기능들 (모두 유지) ===
  
  // 친구 요청 수락
  const acceptFriend = async (notif) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, "notifications", notif.id), { status: "accepted" });
      await addDoc(collection(db, "friends"), {
        userA: notif.from,
        userB: notif.to,
        createdAt: serverTimestamp(),
      });
      
      // 알림을 목록에서 제거
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      
      // 수락 완료 알림을 상대방에게 발송
      await addDoc(collection(db, "notifications"), {
        type: "friend_request_accepted",
        from: user.id,
        to: notif.from,
        fromNickname: user.nickname,
        status: "unread",
        createdAt: serverTimestamp(),
        message: `${user.nickname}님이 친구 요청을 수락했습니다!`
      });
      
    } catch (error) {
      console.error("친구 수락 실패:", error);
      alert("친구 수락에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  // 친구 요청 거절
  const rejectFriend = async (notif) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, "notifications", notif.id), { status: "rejected" });
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setActiveRejectId("");
      setRejectReason("");
    } catch (error) {
      console.error("친구 거절 실패:", error);
      alert("친구 거절에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  // 길드 가입 수락
  const acceptGuildJoin = async (notif) => {
    try {
      await updateDoc(doc(db, "notifications", notif.id), { status: "accepted" });
      
      // guildMembers 컬렉션에 멤버 추가
      await addDoc(collection(db, "guildMembers"), {
        guildId: notif.guildId,
        userId: notif.from,
        nickname: notif.fromNickname,
        role: "임시회원", // 새로운 멤버는 임시회원부터 시작
        tier: "임시회원", // 호환성
        joinDate: serverTimestamp(),
        contribution: 0,
        guildWins: 0,
        guildLosses: 0
      });

      // 사용자 정보에도 길드 정보 업데이트 (호환성)
      await updateDoc(doc(db, "users", notif.from), {
        guildId: notif.guildId,
        guildName: notif.guildName,
        guildTier: "임시회원",
        joinedGuildAt: serverTimestamp()
      });

      // 가입 승인 알림을 신청자에게 발송
      await addDoc(collection(db, "notifications"), {
        type: "guild_join_approved",
        from: user.id,
        to: notif.from,
        guildId: notif.guildId,
        guildName: notif.guildName,
        status: "unread",
        createdAt: serverTimestamp(),
        message: `${notif.guildName} 길드 가입이 승인되었습니다!`
      });

      setNotifications(prev => prev.filter(n => n.id !== notif.id));
    } catch (error) {
      console.error("길드 가입 수락 실패:", error);
    }
  };

  // 길드 가입 거절
  const rejectGuildJoin = async (notif) => {
    if (!rejectReason.trim()) {
      alert("거절 사유를 입력하세요.");
      return;
    }
    try {
      await updateDoc(doc(db, "notifications", notif.id), {
        status: "rejected",
        rejectReason,
      });
      
      // 가입 거절 알림을 신청자에게 발송
      await addDoc(collection(db, "notifications"), {
        type: "guild_join_rejected",
        from: user.id,
        to: notif.from,
        guildId: notif.guildId,
        guildName: notif.guildName,
        status: "unread",
        createdAt: serverTimestamp(),
        message: `${notif.guildName} 길드 가입이 거절되었습니다.`,
        rejectReason
      });

      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setRejectReason("");
      setActiveRejectId("");
    } catch (error) {
      console.error("길드 가입 거절 실패:", error);
    }
  };

  // === 새로운 길드 관련 기능들 ===

  // 길드 초대 수락/거절
  const handleGuildInvitation = async (notif, action) => {
    try {
      await updateDoc(doc(db, "notifications", notif.id), { 
        status: action === "accept" ? "accepted" : "rejected" 
      });
      
      if (action === "accept") {
        // guildMembers 컬렉션에 멤버 추가
        await addDoc(collection(db, "guildMembers"), {
          guildId: notif.guildId,
          userId: notif.to,
          nickname: user.nickname,
          role: "정회원", // 초대받은 멤버는 정회원으로 시작
          tier: "정회원",
          joinDate: serverTimestamp(),
          contribution: 0,
          guildWins: 0,
          guildLosses: 0
        });

        // 사용자 정보 업데이트
        await updateDoc(doc(db, "users", notif.to), {
          guildId: notif.guildId,
          guildName: notif.guildName,
          guildTier: "정회원",
          joinedGuildAt: serverTimestamp()
        });

        navigate("/guild");
      }
      
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setShowDropdown(false);
    } catch (error) {
      console.error("길드 초대 처리 실패:", error);
    }
  };

  // 길드 등급 변경 알림 확인
  const acknowledgeTierChange = async (notif) => {
    try {
      await updateDoc(doc(db, "notifications", notif.id), { 
        status: "read",
        readAt: serverTimestamp()
      });
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
    } catch (error) {
      console.error("등급 변경 알림 확인 실패:", error);
    }
  };

  // 길드 매치 요청 수락
  const acceptGuildMatch = async (notif) => {
    try {
      await updateDoc(doc(db, "notifications", notif.id), { status: "accepted" });
      
      // 매치 요청 수락 처리
      if (notif.matchRequestId) {
        await updateDoc(doc(db, "guildMatchRequests", notif.matchRequestId), {
          status: "matched",
          matchedWith: notif.toGuildId,
          matchedAt: serverTimestamp()
        });
        
        // 실제 매치 생성
        const matchRef = await addDoc(collection(db, "guildMatches"), {
          guild1Id: notif.fromGuildId,
          guild1Name: notif.fromGuildName,
          guild2Id: notif.toGuildId,
          guild2Name: notif.toGuildName,
          sport: notif.sport,
          status: "scheduled",
          createdAt: serverTimestamp(),
          matchType: notif.matchType || "ranking"
        });

        setNotifications(prev => prev.filter(n => n.id !== notif.id));
        setShowDropdown(false);
        navigate(`/guild/match/${matchRef.id}`);
      } else {
        setNotifications(prev => prev.filter(n => n.id !== notif.id));
        setShowDropdown(false);
        navigate("/guild");
      }
    } catch (error) {
      console.error("길드 매치 수락 실패:", error);
    }
  };

  // 길드 매치 거절
  const rejectGuildMatch = async (notif) => {
    if (!rejectReason.trim()) {
      alert("거절 사유를 입력하세요.");
      return;
    }
    try {
      await updateDoc(doc(db, "notifications", notif.id), {
        status: "rejected",
        rejectReason,
      });

      // 매치 요청 거절 알림을 신청 길드에 발송
      await addDoc(collection(db, "notifications"), {
        type: "guild_match_rejected",
        matchType: notif.matchType,
        fromGuildId: notif.toGuildId,
        fromGuildName: notif.toGuildName,
        toGuildId: notif.fromGuildId,
        to: notif.fromGuildLeaderId,
        status: "unread",
        matchRequestId: notif.matchRequestId,
        createdAt: serverTimestamp(),
        rejectReason,
        message: `${notif.toGuildName} 길드가 ${notif.matchType} 요청을 거절했습니다.`
      });

      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setRejectReason("");
      setActiveRejectId("");
    } catch (error) {
      console.error("길드 매치 거절 실패:", error);
    }
  };

  // === 협회/대회 시스템 기능들 ===

  // 대회 참가 승인/거절 (협회용)
  const handleTournamentParticipation = async (notif, action) => {
    try {
      await updateDoc(doc(db, "notifications", notif.id), { 
        status: action === "accept" ? "accepted" : "rejected" 
      });
      
      if (action === "accept") {
        // 참가자 목록에 추가
        await addDoc(collection(db, "tournamentParticipants"), {
          tournamentId: notif.tournamentId,
          userId: notif.from,
          userNickname: notif.fromNickname,
          guildId: notif.guildId || null,
          registeredAt: serverTimestamp(),
          status: "confirmed"
        });
        
        // 참가 확정 알림을 신청자에게 발송
        await addDoc(collection(db, "notifications"), {
          type: "tournament_participation_confirmed",
          from: user.id,
          to: notif.from,
          tournamentId: notif.tournamentId,
          tournamentName: notif.tournamentName,
          status: "unread",
          createdAt: serverTimestamp(),
          message: `${notif.tournamentName} 대회 참가가 승인되었습니다!`
        });
      } else {
        // 참가 거절 알림을 신청자에게 발송
        await addDoc(collection(db, "notifications"), {
          type: "tournament_participation_rejected",
          from: user.id,
          to: notif.from,
          tournamentId: notif.tournamentId,
          tournamentName: notif.tournamentName,
          status: "unread",
          createdAt: serverTimestamp(),
          message: `${notif.tournamentName} 대회 참가가 거절되었습니다.`
        });
      }
      
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setShowDropdown(false);
    } catch (error) {
      console.error("대회 참가 처리 실패:", error);
    }
  };

  // 대회 결과 승인 (협회용)
  const approveTournamentResult = async (notif) => {
    try {
      await updateDoc(doc(db, "notifications", notif.id), { 
        status: "accepted",
        approvedAt: serverTimestamp()
      });
      
      // 대회 결과를 공식 기록으로 승인
      if (notif.matchId) {
        await updateDoc(doc(db, "tournamentMatches", notif.matchId), {
          status: "completed",
          approvedAt: serverTimestamp(),
          approvedBy: user.id
        });
      }
      
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setShowDropdown(false);
    } catch (error) {
      console.error("대회 결과 승인 실패:", error);
    }
  };

  // 대회 대진표 확정 알림 처리
  const acknowledgeBracket = async (notif) => {
    try {
      await updateDoc(doc(db, "notifications", notif.id), { 
        status: "read",
        readAt: serverTimestamp()
      });
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setShowDropdown(false);
      navigate(`/tournament/${notif.tournamentId}/bracket`);
    } catch (error) {
      console.error("대진표 확인 실패:", error);
    }
  };

  // === 매칭 시스템 기능들 ===

  // 매칭 요청 수락
  const acceptMatchRequest = async (notif) => {
    try {
      await updateDoc(doc(db, "notifications", notif.id), { status: "accepted" });
      
      if (notif.matchId) {
        // 매치 상태 업데이트
        await updateDoc(doc(db, "matches", notif.matchId), {
          status: "accepted",
          acceptedAt: serverTimestamp()
        });
        
        setNotifications(prev => prev.filter(n => n.id !== notif.id));
        setShowDropdown(false);
        navigate(`/match/${notif.matchId}`);
      }
    } catch (error) {
      console.error("매치 요청 수락 실패:", error);
    }
  };

  // 매칭 요청 거절
  const rejectMatchRequest = async (notif) => {
    try {
      await updateDoc(doc(db, "notifications", notif.id), { status: "rejected" });
      
      if (notif.matchId) {
        await updateDoc(doc(db, "matches", notif.matchId), {
          status: "rejected",
          rejectedAt: serverTimestamp()
        });
      }
      
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
    } catch (error) {
      console.error("매치 요청 거절 실패:", error);
    }
  };

  // === 유틸리티 함수들 ===

  // 모든 알림을 읽음으로 표시
  const markAllAsRead = async () => {
    if (markingAsRead) return;
    
    try {
      setMarkingAsRead(true);
      const unreadNotifs = notifications.filter(n => n.status === "unread");
      
      const updatePromises = unreadNotifs.map(notif => 
        updateDoc(doc(db, "notifications", notif.id), { status: "read" })
      );
      
      await Promise.all(updatePromises);
      setNotifications(prev => prev.filter(n => n.status !== "unread"));
    } catch (error) {
      console.error("알림 읽음 처리 실패:", error);
    } finally {
      setMarkingAsRead(false);
    }
  };

  // 알림 삭제
  const deleteNotification = async (notifId) => {
    try {
      await deleteDoc(doc(db, "notifications", notifId));
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (error) {
      console.error("알림 삭제 실패:", error);
    }
  };

  // 시간 형식화
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "방금 전";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`;
    return date.toLocaleDateString();
  };

  // 알림 타입별 아이콘 및 색상
  const getNotificationIcon = (type) => {
    const icons = {
      // 기존 알림들
      friend_request: "🤝",
      friend_request_accepted: "✅",
      friend_request_rejected: "❌",
      guild_join_request: "🏰",
      guild_invitation: "📧",
      guild_match_request: "⚔️",
      guild_match_rejected: "⚠️",
      
      // 새로운 길드 관련 알림들
      guild_join_approved: "✅",
      guild_join_rejected: "❌",
      guild_tier_change: "📈",
      guild_announcement: "📢",
      guild_match_result: "🏆",
      guild_friendly_request: "🤝",
      guild_ranking_found: "⚔️",
      
      // 협회/대회 알림들
      tournament_registration: "🏆",
      tournament_invitation: "🎯",
      tournament_result_approval: "📊",
      tournament_bracket_update: "📋",
      tournament_participation_confirmed: "✅",
      tournament_participation_rejected: "❌",
      tournament_match_scheduled: "📅",
      tournament_result_updated: "📊",
      
      // 매칭 시스템 알림들
      match_request: "🎮",
      friendly_match_request: "🤝",
      ranking_match_found: "⚔️",
      match_result_confirmation: "📊",
      match_cancelled: "❌",
      
      // 시스템 알림들
      system_announcement: "📢",
      maintenance_notice: "🔧",
      update_notice: "🔄",
      reward_received: "🎁"
    };
    return icons[type] || "🔔";
  };

  const getNotificationColor = (type) => {
    const colors = {
      friend_request: "#4caf50",
      friend_request_accepted: "#4caf50",
      friend_request_rejected: "#f44336",
      guild_join_request: "#9c27b0",
      guild_invitation: "#2196f3",
      guild_match_request: "#f44336",
      tournament_registration: "#ff9800",
      match_request: "#673ab7",
      system_announcement: "#607d8b"
    };
    return colors[type] || "#666";
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes bell-shake {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(-10deg); }
            75% { transform: rotate(10deg); }
          }
          .notification-bell:hover {
            animation: bell-shake 0.5s ease-in-out;
          }
        `}
      </style>
      
      <div ref={bellRef} style={{ display: "inline-block", position: "relative" }}>
        <span
          onClick={handleBellClick}
          className="notification-bell"
          style={{
            fontSize: 26,
            cursor: "pointer",
            position: "relative",
            display: "inline-block",
            transition: "transform 0.2s",
            transform: showDropdown ? "scale(1.1)" : "scale(1)"
          }}
          title="알림"
        >
          <span role="img" aria-label="알림">🔔</span>
          {notifications.length > 0 && (
            <span
              style={{
                background: "linear-gradient(135deg, #ff4757, #ff3742)",
                color: "#fff",
                fontSize: 12,
                borderRadius: "50%",
                padding: "3px 6px",
                fontWeight: 700,
                position: "absolute",
                right: -12,
                top: -8,
                minWidth: "18px",
                textAlign: "center",
                boxShadow: "0 2px 4px rgba(255,71,87,0.3)",
                animation: notifications.length > 5 ? "pulse 2s infinite" : "none"
              }}
            >
              {notifications.length > 99 ? "99+" : notifications.length}
            </span>
          )}
        </span>

        {showDropdown && (
          <div
            style={{
              position: "absolute",
              top: 40,
              right: 0,
              background: "#fff",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 16,
              minWidth: 420,
              maxWidth: 480,
              zIndex: 9999,
              padding: 0,
              color: "#222",
              border: "1px solid #e1e8ed",
              maxHeight: "70vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }}
          >
            {/* 헤더 */}
            <div style={{
              padding: "16px 20px",
              borderBottom: "1px solid #f1f3f4",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#fff",
              borderRadius: "16px 16px 0 0"
            }}>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center" 
              }}>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: 16, 
                  fontWeight: 700 
                }}>알림</h3>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ 
                    fontSize: 14, 
                    opacity: 0.9 
                  }}>
                    {notifications.length}개
                  </span>
                  {notifications.length > 0 && (
                    <button
                      onClick={markAllAsRead}
                      disabled={markingAsRead}
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        color: "#fff",
                        borderRadius: 8,
                        padding: "4px 8px",
                        fontSize: 12,
                        cursor: markingAsRead ? "not-allowed" : "pointer",
                        opacity: markingAsRead ? 0.6 : 1
                      }}
                    >
                      {markingAsRead ? "처리중..." : "모두 읽음"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 알림 목록 */}
            <div style={{
              maxHeight: "500px",
              overflowY: "auto",
              padding: "8px 0"
            }}>
              {loading ? (
                <div style={{ 
                  padding: "20px", 
                  textAlign: "center", 
                  color: "#666",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8
                }}>
                  <div style={{
                    width: 20,
                    height: 20,
                    border: "2px solid #f3f3f3",
                    borderTop: "2px solid #3498db",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }}></div>
                  로딩 중...
                </div>
              ) : notifications.length === 0 ? (
                <div style={{ 
                  padding: "40px 20px", 
                  textAlign: "center", 
                  color: "#999" 
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🔕</div>
                  새로운 알림이 없습니다.
                </div>
              ) : (
                notifications.map((n) => (
                  <div key={n.id} style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #f8f9fa",
                    fontSize: 14,
                    lineHeight: 1.4,
                    transition: "background-color 0.2s",
                    cursor: "pointer",
                    position: "relative",
                    backgroundColor: n.status === "unread" ? "#f0f8ff" : "transparent"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = n.status === "unread" ? "#f0f8ff" : "transparent"}
                  onClick={() => {
                    // 단순 알림(수락/거절 버튼이 없는)들만 클릭시 읽음 처리
                    const simpleNotificationTypes = [
                      "guild_join_approved", "guild_join_rejected", "guild_tier_change", 
                      "guild_announcement", "tournament_participation_confirmed", 
                      "tournament_participation_rejected", "friend_request_accepted",
                      "system_announcement", "maintenance_notice", "update_notice",
                      "reward_received", "match_cancelled", "guild_match_rejected"
                    ];
                    
                    if (simpleNotificationTypes.includes(n.type)) {
                      markAsRead(n.id);
                    }
                  }}
                  >
                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12
                    }}>
                      <span style={{ 
                        fontSize: 20, 
                        flexShrink: 0,
                        color: getNotificationColor(n.type)
                      }}>
                        {getNotificationIcon(n.type)}
                      </span>
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* 친구 요청 수락 완료 */}
                        {n.type === "friend_request_accepted" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: "#4caf50" }}>
                              {n.message}
                            </div>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/addfriend");
                                markAsRead(n.id);
                              }}
                              style={{
                                ...acceptButtonStyle,
                                background: "linear-gradient(135deg, #2196f3, #1976d2)"
                              }}
                            >
                              친구 목록 보기
                            </button>
                          </>
                        )}

                        {/* 친구 요청 */}
                        {n.type === "friend_request" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#1976d2" }}>{n.fromNickname || n.from}</span>님이 
                              친구 요청을 보냈습니다.
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button onClick={() => acceptFriend(n)} style={acceptButtonStyle}>
                                수락
                              </button>
                              <button onClick={() => rejectFriend(n)} style={rejectButtonStyle}>
                                거절
                              </button>
                            </div>
                          </>
                        )}

                        {/* 길드 가입 신청 */}
                        {n.type === "guild_join_request" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#7b1fa2" }}>{n.fromNickname || n.from}</span>님이 
                              <span style={{ color: "#1976d2" }}> {n.guildName}</span> 길드 가입을 신청했습니다.
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button onClick={() => acceptGuildJoin(n)} style={acceptButtonStyle}>
                                수락
                              </button>
                              <button
                                onClick={() => {
                                  setActiveRejectId(n.id);
                                  setRejectReason("");
                                }}
                                style={rejectButtonStyle}
                              >
                                거절
                              </button>
                            </div>
                            {activeRejectId === n.id && (
                              <div style={{ marginTop: 8 }}>
                                <input
                                  placeholder="거절 사유(필수)"
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                  style={inputStyle}
                                />
                                <button
                                  onClick={() => rejectGuildJoin(n)}
                                  style={{ ...rejectButtonStyle, marginTop: 4 }}
                                >
                                  거절 확정
                                </button>
                              </div>
                            )}
                          </>
                        )}

                        {/* 길드 초대 */}
                        {n.type === "guild_invitation" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#1976d2" }}>{n.guildName}</span> 길드에서 
                              초대장을 보냈습니다.
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button 
                                onClick={() => handleGuildInvitation(n, "accept")} 
                                style={acceptButtonStyle}
                              >
                                수락
                              </button>
                              <button 
                                onClick={() => handleGuildInvitation(n, "reject")} 
                                style={rejectButtonStyle}
                              >
                                거절
                              </button>
                            </div>
                          </>
                        )}

                        {/* 길드 가입 승인/거절 결과 */}
                        {(n.type === "guild_join_approved" || n.type === "guild_join_rejected") && (
                          <>
                            <div style={{ 
                              fontWeight: 600, 
                              marginBottom: 4,
                              color: n.type === "guild_join_approved" ? "#388e3c" : "#d32f2f"
                            }}>
                              {n.message}
                            </div>
                            {n.type === "guild_join_approved" && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/guild");
                                  markAsRead(n.id);
                                }}
                                style={{
                                  ...acceptButtonStyle,
                                  background: "linear-gradient(135deg, #2196f3, #1976d2)"
                                }}
                              >
                                길드 보기
                              </button>
                            )}
                            {n.rejectReason && (
                              <div style={{ 
                                color: "#666", 
                                fontSize: 12, 
                                marginTop: 4,
                                fontStyle: "italic" 
                              }}>
                                사유: {n.rejectReason}
                              </div>
                            )}
                          </>
                        )}

                        {/* 길드 등급 변경 알림 */}
                        {n.type === "guild_tier_change" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: "#ff6f00" }}>
                              길드 등급이 <span style={{ color: "#1976d2" }}>{n.newTier}</span>로 변경되었습니다!
                            </div>
                            {n.newRole && (
                              <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                                역할: {n.newRole}
                              </div>
                            )}
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                acknowledgeTierChange(n);
                              }}
                              style={{
                                ...acceptButtonStyle,
                                background: "linear-gradient(135deg, #ff9800, #f57c00)"
                              }}
                            >
                              확인
                            </button>
                          </>
                        )}

                        {/* 길드 단체 알림 */}
                        {n.type === "guild_announcement" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: "#7b1fa2" }}>
                              [{n.guildName}] 길드 공지
                            </div>
                            <div style={{ fontSize: 13, color: "#666", lineHeight: 1.4 }}>
                              {n.message}
                            </div>
                          </>
                        )}

                        {/* 길드 매치 신청 */}
                        {n.type === "guild_match_request" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#d32f2f" }}>{n.fromGuildName}</span> 길드에서 
                              <span style={{ color: "#1976d2" }}> {n.matchType || "길드전"}</span>을 신청했습니다.
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              종목: {n.sport} | 지역: {n.region}
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button onClick={() => acceptGuildMatch(n)} style={acceptButtonStyle}>
                                수락
                              </button>
                              <button
                                onClick={() => {
                                  setActiveRejectId(n.id);
                                  setRejectReason("");
                                }}
                                style={rejectButtonStyle}
                              >
                                거절
                              </button>
                            </div>
                            {activeRejectId === n.id && (
                              <div style={{ marginTop: 8 }}>
                                <input
                                  placeholder="거절 사유(필수)"
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                  style={inputStyle}
                                />
                                <button
                                  onClick={() => rejectGuildMatch(n)}
                                  style={{ ...rejectButtonStyle, marginTop: 4 }}
                                >
                                  거절 확정
                                </button>
                              </div>
                            )}
                          </>
                        )}

                        {/* 길드 매치 거절 알림 */}
                        {n.type === "guild_match_rejected" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: "#d32f2f" }}>
                              <span style={{ color: "#1976d2" }}>{n.fromGuildName}</span> 길드가 
                              <span style={{ color: "#d32f2f" }}> {n.matchType}</span> 요청을 거절했습니다.
                            </div>
                            {n.rejectReason && (
                              <div style={{ 
                                color: "#666", 
                                fontSize: 12, 
                                marginTop: 4,
                                fontStyle: "italic" 
                              }}>
                                사유: {n.rejectReason}
                              </div>
                            )}
                          </>
                        )}

                        {/* 길드 친선전 요청 */}
                        {n.type === "guild_friendly_request" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#4caf50" }}>{n.fromGuildName}</span> 길드에서 
                              <span style={{ color: "#2196f3" }}> 친선전</span>을 신청했습니다.
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button onClick={() => acceptGuildMatch(n)} style={acceptButtonStyle}>
                                수락
                              </button>
                              <button onClick={() => rejectGuildMatch(n)} style={rejectButtonStyle}>
                                거절
                              </button>
                            </div>
                          </>
                        )}

                        {/* 길드 랭킹전 매칭 완료 */}
                        {n.type === "guild_ranking_found" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#ff6f00" }}>길드 랭킹전</span> 상대를 찾았습니다!
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              상대: {n.opponentGuildName} | 종목: {n.sport}
                            </div>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/guild/match/${n.matchId}`);
                                markAsRead(n.id);
                              }}
                              style={{
                                ...acceptButtonStyle,
                                background: "linear-gradient(135deg, #ff9800, #f57c00)"
                              }}
                            >
                              경기 참여
                            </button>
                          </>
                        )}

                        {/* 대회 참가 신청 (협회용) */}
                        {n.type === "tournament_registration" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#ff6f00" }}>{n.fromNickname || n.from}</span>님이 
                              <span style={{ color: "#1976d2" }}> {n.tournamentName}</span> 대회 참가를 신청했습니다.
                            </div>
                            {n.guildName && (
                              <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                                소속 길드: {n.guildName}
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button 
                                onClick={() => handleTournamentParticipation(n, "accept")} 
                                style={acceptButtonStyle}
                              >
                                승인
                              </button>
                              <button 
                                onClick={() => handleTournamentParticipation(n, "reject")} 
                                style={rejectButtonStyle}
                              >
                                거절
                              </button>
                            </div>
                          </>
                        )}

                        {/* 대회 참가 확정/거절 알림 */}
                        {(n.type === "tournament_participation_confirmed" || n.type === "tournament_participation_rejected") && (
                          <>
                            <div style={{ 
                              fontWeight: 600, 
                              marginBottom: 4,
                              color: n.type === "tournament_participation_confirmed" ? "#388e3c" : "#d32f2f"
                            }}>
                              {n.message}
                            </div>
                            {n.type === "tournament_participation_confirmed" && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/tournament/${n.tournamentId}`);
                                  markAsRead(n.id);
                                }}
                                style={{
                                  ...acceptButtonStyle,
                                  background: "linear-gradient(135deg, #2196f3, #1976d2)"
                                }}
                              >
                                대회 보기
                              </button>
                            )}
                          </>
                        )}

                        {/* 대회 초대 */}
                        {n.type === "tournament_invitation" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#9c27b0" }}>{n.tournamentName}</span> 대회에 
                              초대되었습니다!
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              주최: {n.organizerName} | 일정: {n.tournamentDate}
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/tournament/${n.tournamentId}/register`);
                                  markAsRead(n.id);
                                }}
                                style={acceptButtonStyle}
                              >
                                참가 신청
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNotification(n.id);
                                }}
                                style={rejectButtonStyle}
                              >
                                거절
                              </button>
                            </div>
                          </>
                        )}

                        {/* 대회 결과 승인 요청 (협회용) */}
                        {n.type === "tournament_result_approval" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#388e3c" }}>{n.tournamentName}</span> 대회 
                              경기 결과 승인이 필요합니다.
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              {n.matchInfo}
                            </div>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                approveTournamentResult(n);
                              }}
                              style={acceptButtonStyle}
                            >
                              결과 승인
                            </button>
                          </>
                        )}

                        {/* 대회 대진표 확정 알림 */}
                        {n.type === "tournament_bracket_update" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#388e3c" }}>{n.tournamentName}</span> 대회 
                              대진표가 확정되었습니다!
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              대진표를 확인하고 경기 일정을 체크하세요.
                            </div>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                acknowledgeBracket(n);
                              }}
                              style={{
                                ...acceptButtonStyle,
                                background: "linear-gradient(135deg, #9c27b0, #7b1fa2)"
                              }}
                            >
                              대진표 보기
                            </button>
                          </>
                        )}

                        {/* 대회 경기 일정 알림 */}
                        {n.type === "tournament_match_scheduled" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#2196f3" }}>{n.tournamentName}</span> 
                              경기 일정이 확정되었습니다.
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              일시: {n.matchDate} | 상대: {n.opponentName}
                            </div>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/tournament/${n.tournamentId}/match/${n.matchId}`);
                                markAsRead(n.id);
                              }}
                              style={{
                                ...acceptButtonStyle,
                                background: "linear-gradient(135deg, #2196f3, #1976d2)"
                              }}
                            >
                              경기 확인
                            </button>
                          </>
                        )}

                        {/* 개인 매치 요청 */}
                        {n.type === "match_request" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#1976d2" }}>{n.fromNickname}</span>님이 
                              <span style={{ color: "#673ab7" }}> 볼링 경기</span>를 신청했습니다.
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              지역: {n.region} | 선호 날짜: {n.preferredDate}
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  acceptMatchRequest(n);
                                }}
                                style={acceptButtonStyle}
                              >
                                수락
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  rejectMatchRequest(n);
                                }}
                                style={rejectButtonStyle}
                              >
                                거절
                              </button>
                            </div>
                          </>
                        )}

                        {/* 친선전 요청 */}
                        {n.type === "friendly_match_request" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#1976d2" }}>{n.fromNickname}</span>님이 
                              <span style={{ color: "#4caf50" }}> 친선전</span>을 신청했습니다.
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              종목: 볼링 | MMR: {n.fromMMR}
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  acceptMatchRequest(n);
                                }}
                                style={acceptButtonStyle}
                              >
                                수락
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  rejectMatchRequest(n);
                                }}
                                style={rejectButtonStyle}
                              >
                                거절
                              </button>
                            </div>
                          </>
                        )}

                        {/* 랭킹전 매칭 완료 */}
                        {n.type === "ranking_match_found" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#ff6f00" }}>랭킹전</span> 상대를 찾았습니다!
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              상대: {n.opponentNickname} (MMR: {n.opponentMMR})
                            </div>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/match/${n.matchId}`);
                                markAsRead(n.id);
                              }}
                              style={{
                                ...acceptButtonStyle,
                                background: "linear-gradient(135deg, #ff9800, #f57c00)"
                              }}
                            >
                              경기 참여
                            </button>
                          </>
                        )}

                        {/* 경기 결과 확인 요청 */}
                        {n.type === "match_result_confirmation" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              <span style={{ color: "#1976d2" }}>{n.fromNickname}</span>님이 
                              경기 결과 확인을 요청했습니다.
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              결과: {n.resultSummary}
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/match/${n.matchId}/result`);
                                  markAsRead(n.id);
                                }}
                                style={acceptButtonStyle}
                              >
                                결과 확인
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNotification(n.id);
                                }}
                                style={rejectButtonStyle}
                              >
                                이의 제기
                              </button>
                            </div>
                          </>
                        )}

                        {/* 경기 취소 알림 */}
                        {n.type === "match_cancelled" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: "#d32f2f" }}>
                              <span style={{ color: "#1976d2" }}>{n.fromNickname}</span>님이 
                              경기를 취소했습니다.
                            </div>
                            {n.cancelReason && (
                              <div style={{ 
                                color: "#666", 
                                fontSize: 12, 
                                marginTop: 4,
                                fontStyle: "italic" 
                              }}>
                                사유: {n.cancelReason}
                              </div>
                            )}
                          </>
                        )}

                        {/* 보상 수령 알림 */}
                        {n.type === "reward_received" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: "#ff6f00" }}>
                              🎁 보상을 받았습니다!
                            </div>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                              {n.rewardType}: {n.rewardAmount} | 사유: {n.rewardReason}
                            </div>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/mypage");
                                markAsRead(n.id);
                              }}
                              style={{
                                ...acceptButtonStyle,
                                background: "linear-gradient(135deg, #ff9800, #f57c00)"
                              }}
                            >
                              확인하기
                            </button>
                          </>
                        )}

                        {/* 시스템 공지 */}
                        {n.type === "system_announcement" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: "#ff6f00" }}>
                              {n.title || "시스템 알림"}
                            </div>
                            <div style={{ color: "#666", fontSize: 12, lineHeight: 1.4 }}>
                              {n.message}
                            </div>
                            {n.actionUrl && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(n.actionUrl, '_blank');
                                  markAsRead(n.id);
                                }}
                                style={{
                                  ...acceptButtonStyle,
                                  background: "linear-gradient(135deg, #607d8b, #455a64)",
                                  marginTop: 8
                                }}
                              >
                                자세히 보기
                              </button>
                            )}
                          </>
                        )}

                        {/* 점검 공지 */}
                        {n.type === "maintenance_notice" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: "#f57c00" }}>
                              🔧 서버 점검 안내
                            </div>
                            <div style={{ color: "#666", fontSize: 12, lineHeight: 1.4 }}>
                              점검 시간: {n.maintenanceTime}<br/>
                              예상 소요: {n.duration}
                            </div>
                          </>
                        )}

                        {/* 업데이트 공지 */}
                        {n.type === "update_notice" && (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: "#2196f3" }}>
                              🔄 새로운 업데이트
                            </div>
                            <div style={{ color: "#666", fontSize: 12, lineHeight: 1.4 }}>
                              {n.updateContent}
                            </div>
                            {n.updateUrl && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(n.updateUrl, '_blank');
                                  markAsRead(n.id);
                                }}
                                style={{
                                  ...acceptButtonStyle,
                                  background: "linear-gradient(135deg, #2196f3, #1976d2)",
                                  marginTop: 8
                                }}
                              >
                                업데이트 내용 보기
                              </button>
                            )}
                          </>
                        )}

                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 8
                        }}>
                          <div style={{ 
                            fontSize: 11, 
                            color: "#999"
                          }}>
                            {formatTime(n.createdAt)}
                          </div>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(n.id);
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#ccc",
                              cursor: "pointer",
                              padding: "4px",
                              borderRadius: 4,
                              fontSize: 12,
                              opacity: 0.6,
                              transition: "opacity 0.2s"
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = 1}
                            onMouseLeave={(e) => e.target.style.opacity = 0.6}
                            title="알림 삭제"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* 푸터 */}
            <div style={{
              padding: "12px 20px",
              borderTop: "1px solid #f1f3f4",
              background: "#fafafa",
              borderRadius: "0 0 16px 16px",
              display: "flex",
              gap: 8
            }}>
              <button
                onClick={() => setShowDropdown(false)}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "1px solid #ddd",
                  color: "#666",
                  padding: "8px",
                  borderRadius: 8,
                  fontSize: 13,
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#f5f5f5";
                  e.target.style.borderColor = "#bbb";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.borderColor = "#ddd";
                }}
              >
                닫기
              </button>
              
              {notifications.length > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={markingAsRead}
                  style={{
                    flex: 1,
                    background: markingAsRead ? "#f5f5f5" : "linear-gradient(135deg, #667eea, #764ba2)",
                    border: "none",
                    color: markingAsRead ? "#999" : "#fff",
                    padding: "8px",
                    borderRadius: 8,
                    fontSize: 13,
                    cursor: markingAsRead ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                    opacity: markingAsRead ? 0.6 : 1
                  }}
                >
                  {markingAsRead ? "처리 중..." : "모두 읽음"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default NotificationBell;